import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Stack, Group, Card, Text, Button, SimpleGrid, Center, Loader, Box, Alert,
} from '@mantine/core';
import { Users, Layers, Smartphone, Trophy, Plus, QrCode } from 'lucide-react';

import { gamesApi, groupsApi, membersApi, devicesApi } from '../api';
import { StatTile } from '../components/StatTile';
import { Eyebrow } from '../components/Eyebrow';
import { RunningGameCard } from '../components/RunningGameCard';
import { plural } from '../utils/format';
import { useActiveEpoch } from '../hooks/useActiveEpoch';

export default function DashboardPage() {
  const navigate = useNavigate();

  // Přehled ukazuje jen aktivní epochu — jinak by mezi běžícími hrami mohla
  // vyskočit hra z jiného tábora.
  const { epoch, isLoading: epochLoading } = useActiveEpoch();
  const games = useQuery({
    queryKey: ['games', epoch?.id],
    queryFn: () => gamesApi.getAll(epoch!.id),
    enabled: !!epoch,
  });
  const members = useQuery({ queryKey: ['members'], queryFn: () => membersApi.getAll() });
  const groups = useQuery({ queryKey: ['groups'], queryFn: groupsApi.getAll });
  const devices = useQuery({ queryKey: ['devices'], queryFn: devicesApi.getAll });

  if (epochLoading || members.isLoading || groups.isLoading || (epoch && games.isLoading)) {
    return <Center h={240}><Loader /></Center>;
  }

  if (!epoch) {
    return (
      <Alert color="orange" title="Žádná aktivní epocha">
        Bez aktivní epochy nejsou data, ke kterým by se zařízení v terénu hlásila.
        Aktivuj ji v Nastavení → Epochy.
      </Alert>
    );
  }

  // Běžet jich může víc naráz (třeba mrtvoliště jako overlay nad jinou hrou),
  // tak se ukazují všechny, ne jen první nalezená.
  const running = (games.data ?? []).filter((g) => !g.ended_at);
  const withoutNumber = (members.data ?? []).filter((m) => m.number === null).length;
  const pairedDevices = (devices.data ?? []).filter((d) => d.status === 'COMPLETED').length;

  return (
    <Stack gap="sm">
      {running.length === 0 ? (
        <Card>
          <Group justify="space-between" wrap="wrap">
            <Box>
              <Eyebrow>Terén</Eyebrow>
              <Text fw={700} fz="lg">Žádná hra neběží</Text>
              <Text c="dimmed" fz="sm">Založ hru a zařízení v terénu se k ní přihlásí.</Text>
            </Box>
            <Button leftSection={<Plus size={14} />} onClick={() => navigate('/hry')}>Založit hru</Button>
          </Group>
        </Card>
      ) : (
        <Stack gap="sm">
          {running.length > 1 && (
            <Eyebrow>Právě běží {running.length} her</Eyebrow>
          )}
          {running.map((g) => <RunningGameCard key={g.uuid} game={g} />)}
        </Stack>
      )}

      <SimpleGrid cols={{ base: 2, lg: 4 }} spacing="sm">
        <StatTile icon={Users} label="Členové" value={members.data?.length ?? 0}
          hint={withoutNumber > 0 ? `${withoutNumber} bez čísla — mobil je nevidí` : 'všichni mají číslo'} />
        <StatTile icon={Layers} label="Skupiny" value={groups.data?.length ?? 0} />
        <StatTile icon={Smartphone} label="Spárovaná zařízení" value={pairedDevices}
          hint="server neeviduje poslední kontakt" />
        <StatTile icon={Trophy} label="Běžící hry" value={running.length}
          hint={`${plural(games.data?.length ?? 0, 'hra', 'hry', 'her')} celkem`} />
      </SimpleGrid>

      <Card>
        <Group>
          <Button leftSection={<Plus size={14} />} onClick={() => navigate('/hry')}>Nová hra</Button>
          <Button variant="default" leftSection={<QrCode size={14} />} onClick={() => navigate('/zarizeni')}>
            Spárovat zařízení
          </Button>
          <Button variant="default" leftSection={<Users size={14} />} onClick={() => navigate('/clenove')}>
            Přidat člena
          </Button>
        </Group>
      </Card>
    </Stack>
  );
}
